import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import {
  getVerifyStampCollectionCid,
  getVerifyStampTransactionHash,
  hasVerifyStampValue,
} from "../utils/verifyStampLink";

const DownloadStampCertificate = ({ data }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const activeRequest = useRef(null);
  const objectCid = data.objectCid ?? data.object_cid;
  const transactionHash = getVerifyStampTransactionHash(data);
  const collectionCid = getVerifyStampCollectionCid(data);
  const chainId = Number(data.chainId ?? data.chain_id);

  useEffect(() => {
    setLoading(false);
    setError("");
    return () => {
      activeRequest.current?.abort();
      activeRequest.current = null;
    };
  }, [objectCid, transactionHash, collectionCid, chainId]);

  const download = async () => {
    if (activeRequest.current) return;
    const controller = new AbortController();
    activeRequest.current = controller;
    setLoading(true);
    setError("");
    const timeout = setTimeout(() => controller.abort(), 60000);
    try {
      const response = await fetch("/api/v1/stamps/certificate", {
        method: "POST",
        credentials: "omit",
        headers: { "Content-Type": "application/json" },
        signal: controller.signal,
        body: JSON.stringify({
          object_cid: objectCid,
          transaction_hash: transactionHash,
          chain_id: chainId,
          ...(hasVerifyStampValue(collectionCid)
            ? { collection_cid: collectionCid }
            : {}),
        }),
      });
      if (!response.ok) {
        if (activeRequest.current !== controller) return;
        setError(
          {
            400: "The stamp selection is invalid. Please select the stamp again.",
            404: "The selected stamp was not found. Refresh the verification results.",
            409: "Multiple stamps match this selection. Select a specific collection.",
            429: "Too many certificate requests. Please wait a minute and try again.",
          }[response.status] ||
            "Unable to generate the certificate. Please try again.",
        );
        return;
      }
      if (!response.headers.get("Content-Type")?.includes("application/pdf")) {
        throw new Error(
          "Unable to download the certificate. Please try again.",
        );
      }
      const blob = await response.blob();
      if (controller.signal.aborted || activeRequest.current !== controller)
        return;
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `receipt_certificate_${objectCid}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      // Allow the browser to begin reading the object URL before releasing it.
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (failure) {
      if (activeRequest.current === controller) {
        setError(
          failure.name === "AbortError"
            ? "Certificate download timed out. Please try again."
            : "Unable to download the certificate. Please try again.",
        );
      }
    } finally {
      clearTimeout(timeout);
      if (activeRequest.current === controller) {
        activeRequest.current = null;
        setLoading(false);
      }
    }
  };

  return (
    <>
      <button
        type="button"
        className="download-pdf"
        disabled={loading}
        aria-busy={loading}
        onClick={download}
      >
        {loading ? "Generating certificate…" : "Download Stamp Certificate PDF"}
      </button>
      {error && <div role="alert">{error}</div>}
    </>
  );
};

DownloadStampCertificate.propTypes = {
  data: PropTypes.object.isRequired,
};

export default DownloadStampCertificate;
